import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'

import initPayPalFlow from 'reduxModules/utils/paypalFlow'
import addFraudProtection from 'components/utils/addFraudProtection'
import { paypalScriptsVault } from 'components/utils/paypalScripts'
import withAsyncScriptLoader from '../../../../hooks/useScriptAsync'
import './styles.css'

/**
 * SPEC-098 FR-1: the PayPal side of the new-payment-method form on
 * /credit_cards, shown when PayPal is chosen instead of Credit Card. The
 * same Braintree PayPal vault flow as subscription checkout (a billing
 * agreement, so it can pay a subscription later); approving in PayPal is
 * the save, through the same endpoint as a card. Once saved it can be made
 * the default, which also moves the subscription onto it.
 *
 * The save is Turnstile-gated like a card: an approval that lands before
 * the token is held and sent as soon as the token arrives. The captcha
 * widget, cancel and notice are the form's own, below this.
 */
export function AddPayPal({
  clientToken,
  recaptcha,
  savePayPal,
  showFraudProtectionError,
  isScriptLoaded,
  isScriptLoadSucceed
}) {
  const [nonce, setNonce] = useState(null)
  const [saving, setSaving] = useState(false)
  const deviceData = useRef(undefined)
  const active = useRef(true)

  useEffect(() => {
    active.current = true
    return () => {
      active.current = false
    }
  }, [])

  useEffect(() => {
    if (!isScriptLoaded || !isScriptLoadSucceed || !clientToken) return
    addFraudProtection(
      (data) => {
        if (!active.current) return
        deviceData.current = data
        const container = document.getElementById('paypal-button-container')
        if (container) container.innerHTML = ''
        initPayPalFlow(
          clientToken,
          undefined, // vault: no amount
          (approvedNonce) => setNonce(approvedNonce),
          undefined,
          true, // vault flow (billing agreement), as for subscriptions
          undefined,
          undefined,
          undefined,
          undefined,
          { isActive: () => active.current }
        )
      },
      [],
      clientToken,
      showFraudProtectionError
    )
  }, [isScriptLoaded, isScriptLoadSucceed, clientToken])

  // Save once the PayPal approval and a Turnstile token are both in hand
  useEffect(() => {
    if (!nonce || !recaptcha?.token || saving) return
    setSaving(true)
    Promise.resolve(savePayPal(nonce, deviceData.current, recaptcha)).finally(
      () => {
        if (!active.current) return
        setNonce(null)
        setSaving(false)
      }
    )
  }, [nonce, recaptcha?.token])

  return (
    <div className="AddPayPal">
      {isScriptLoaded && !isScriptLoadSucceed && (
        <p className="AddPayPal__error" role="alert">
          PayPal couldn&apos;t load. Please refresh the page and try again.
        </p>
      )}
      {!(isScriptLoaded && !isScriptLoadSucceed) && (
        <p className="AddPayPal__help">
          {saving || nonce
            ? 'Saving your PayPal account…'
            : 'Sign in to PayPal to add your account. You can then make it the payment method for your subscription.'}
        </p>
      )}
      <div id="paypal-button-container" />
      {(saving || nonce) && (
        <span className="field__label--hidden" role="status">
          Saving your PayPal account
        </span>
      )}
    </div>
  )
}

AddPayPal.propTypes = {
  clientToken: PropTypes.string.isRequired,
  recaptcha: PropTypes.shape({
    token: PropTypes.string,
    version: PropTypes.string
  }),
  savePayPal: PropTypes.func.isRequired,
  showFraudProtectionError: PropTypes.func,
  isScriptLoaded: PropTypes.bool,
  isScriptLoadSucceed: PropTypes.bool
}

export default withAsyncScriptLoader(paypalScriptsVault)(AddPayPal)
